import {isFeatureEnabled} from '@github-ui/feature-flags'
import {useState, type Dispatch, type SetStateAction} from 'react'
import type {PreparedPrompt} from '@github-ui/ahp-session-client/contracts'

import {isEnvironmentSelectionStale} from './environment-strip/environment-availability'
import {SANDBOX_ENVIRONMENT_OPTION, type EnvironmentOption} from './environment-strip/environment-option'
import type {Environment} from '@github-ui/agent-sessions-types/environment'
import type {McpPermissionMode} from '@github-ui/agent-sessions-types/mcp-permission-mode'
import type {AgentMode} from '@github-ui/copilot-shared-ui/utils/agent-mode'
import {useEnvironments} from '@github-ui/copilot-shared-ui/environments/use-environments'

export type EnvironmentSubmitInput = {
  environmentId: string
  prompt: PreparedPrompt
  agentMode?: AgentMode
  repositoryNwo?: string
  baseRef?: string
  managedSandbox?: boolean
  /**
   * MCP permission mode to seed on the created session's first turn. Only
   * carried by the Sandbox composer today; the coding-agent API path uses its
   * own `permission_mode` field on `useCreateAPITask`.
   */
  mcpPermissionMode?: McpPermissionMode
  scopeLogin?: string
  space?: {owner: string; slug: string}
}

export type EnvironmentSubmit = (input: EnvironmentSubmitInput) => Promise<unknown>

type UseEnvironmentSelectionOptions = {
  onEnvironmentSubmit?: EnvironmentSubmit
  supportsManagedSandbox?: boolean
  initialEnvironment?: EnvironmentOption | null
  /**
   * The host records the turn for a destination that provisions it later rather
   * than binding an environment itself. Such a host still composes against
   * Sandbox, so its selection is kept even though it has no environment submit
   * and is not on the environments rollout that binding one needs.
   */
  deliversByHandoff?: boolean
}

type UseEnvironmentSelectionResult = {
  selectedEnvironment: EnvironmentOption | null
  setSelectedEnvironment: Dispatch<SetStateAction<EnvironmentOption | null>>
  availableEnvironments: Environment[] | undefined
  isAhpEnvironment: boolean
  isSandboxEnvironment: boolean
  showSandboxOption: boolean
  showEnvironmentPicker: boolean
  isEnvironmentsEnabled: boolean
}

export function isSandboxOptionAvailable({
  isEnvironmentsEnabled,
  isManagedSandboxEnabled,
  supportsManagedSandbox,
  hasEnvironmentSubmit,
}: {
  isEnvironmentsEnabled: boolean
  isManagedSandboxEnabled: boolean
  supportsManagedSandbox: boolean
  hasEnvironmentSubmit: boolean
}): boolean {
  return isEnvironmentsEnabled && isManagedSandboxEnabled && supportsManagedSandbox && hasEnvironmentSubmit
}

export function isSandboxBranchSelectorDisabled(isSandboxEnvironment: boolean): boolean {
  return isSandboxEnvironment && !isFeatureEnabled('copilot_mission_control_sandbox_client_side_clone')
}

/**
 * Whether the draft is being composed against Sandbox.
 *
 * Chat and the organization scope both belong to Sandbox: Actions provisions a
 * repository-backed task and offers neither. A `handoff` delivery does not
 * provision here — it records the turn for a destination that will — so it does
 * not need the environments rollout that *binding* an environment requires.
 * It is still a Sandbox draft, and must follow the Sandbox option exactly as a
 * provisioned one does; otherwise Chat would survive a switch to Actions, where
 * it cannot be started.
 */
export function isSandboxComposedDraft({
  isSandboxEnvironment,
  selectedEnvironmentKind,
  deliversByHandoff,
}: {
  isSandboxEnvironment: boolean
  selectedEnvironmentKind: EnvironmentOption['kind'] | undefined
  deliversByHandoff: boolean
}): boolean {
  return isSandboxEnvironment || (deliversByHandoff && selectedEnvironmentKind === 'sandbox')
}

/**
 * Whether the custom agent picker applies to the selected environment.
 *
 * A custom agent is a coding-agent concept: it names the agent that will run
 * the background task. A Sandbox session has no such agent to choose, so the
 * picker does not belong on that draft — while the mode picker, which chooses
 * how the Sandbox session behaves, still does.
 */
export function isCustomAgentPickerAvailable({
  isSubAgentsEnabled,
  isSandboxEnvironment,
}: {
  isSubAgentsEnabled: boolean
  isSandboxEnvironment: boolean
}): boolean {
  return isSubAgentsEnabled && !isSandboxEnvironment
}

/**
 * Whether the toolbar's image shortcut is redundant with the draft's own attach
 * menu.
 *
 * The shortcut uploads images against the selected repository. Once a Sandbox
 * draft has one, that is the same action the attach menu already offers as a
 * local file, so only the menu is kept. A Sandbox draft with no repository has
 * no upload target for it either way, and every other environment keeps it.
 */
export function isImageAttachShortcutRedundant({
  isSandboxEnvironment,
  hasSelectedRepository,
}: {
  isSandboxEnvironment: boolean
  hasSelectedRepository: boolean
}): boolean {
  return isSandboxEnvironment && hasSelectedRepository
}

/**
 * Whether the premium-request meter applies to the draft's environment.
 *
 * A Sandbox session spends the same premium quota whichever destination the
 * draft is pointed at, so the meter belongs on Sandbox Chat and a Sandbox
 * repository task alike — hiding it on one of them tells the user their
 * remaining requests stopped mattering when they picked a repository. Actions
 * bills a background task elsewhere, so the meter would be reporting a quota
 * that destination does not draw on.
 */
export function isSessionTokenMeterApplicable({
  isTokenBasedBillingEnabled,
  isSandboxEnvironment,
}: {
  isTokenBasedBillingEnabled: boolean
  isSandboxEnvironment: boolean
}): boolean {
  return isTokenBasedBillingEnabled && isSandboxEnvironment
}

/**
 * The environments a host can actually start a session in.
 *
 * A host that creates no tasks runs exactly one thing: a Sandbox session.
 * Listing Actions there would offer a destination whose submit does not exist,
 * so the option is withheld rather than shown and then refused. The picker is
 * still rendered, because which environment the session runs in is precisely
 * what the strip is there to say — and Sandbox is also what makes Chat, the
 * organization scope, and their shared rules apply.
 *
 * Returns `undefined` for a host with a task destination, leaving the picker on
 * its own defaults.
 */
export function resolveEnvironmentOptions({
  hasTaskDestination,
}: {
  hasTaskDestination: boolean
}): EnvironmentOption[] | undefined {
  return hasTaskDestination ? undefined : [SANDBOX_ENVIRONMENT_OPTION]
}

export function useEnvironmentSelection({
  onEnvironmentSubmit,
  supportsManagedSandbox = false,
  initialEnvironment = null,
  deliversByHandoff = false,
}: UseEnvironmentSelectionOptions): UseEnvironmentSelectionResult {
  const isEnvironmentsEnabled = isFeatureEnabled('copilot_mission_control_environments_web_kickoff')
  const isManagedSandboxEnabled = isFeatureEnabled('copilot_mission_control_managed_sandbox_environments')
  const showSandboxOption = isSandboxOptionAvailable({
    isEnvironmentsEnabled,
    isManagedSandboxEnabled,
    supportsManagedSandbox,
    hasEnvironmentSubmit: !!onEnvironmentSubmit,
  })
  // Whether a Sandbox selection may stand. The picker offers Sandbox only when
  // the host can bind it, but a handoff host composes on Sandbox without ever
  // binding it, so dropping its selection would leave the draft on nothing.
  const canSelectSandbox = showSandboxOption || deliversByHandoff
  const [selectedEnvironment, setSelectedEnvironment] = useState<EnvironmentOption | null>(() =>
    initialEnvironment?.kind === 'sandbox' && !canSelectSandbox ? null : initialEnvironment,
  )
  const {data: availableEnvironments} = useEnvironments({enabled: isEnvironmentsEnabled && !!onEnvironmentSubmit})

  if (
    isEnvironmentSelectionStale(selectedEnvironment, availableEnvironments) ||
    (selectedEnvironment?.kind === 'sandbox' && !canSelectSandbox)
  ) {
    setSelectedEnvironment(null)
  }

  const isAhpEnvironment =
    isEnvironmentsEnabled && (selectedEnvironment?.kind === 'ahp' || selectedEnvironment?.kind === 'sandbox')
  const isSandboxEnvironment = isEnvironmentsEnabled && selectedEnvironment?.kind === 'sandbox'
  const showEnvironmentPicker = isEnvironmentsEnabled && !!onEnvironmentSubmit

  return {
    selectedEnvironment,
    setSelectedEnvironment,
    availableEnvironments,
    isAhpEnvironment,
    isSandboxEnvironment,
    showSandboxOption,
    showEnvironmentPicker,
    isEnvironmentsEnabled,
  }
}
